// src/pages/menu/AgaMale.tsx
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FaNotesMedical, FaCheckCircle, FaInfoCircle, FaArrowRight } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { goReserve } from '../../utils/navigation';
import { useMedicinePrices, toSinglePriceItems } from '../../hooks/useMedicinePrices';

interface AgaMalePageProps {
  ipAllowed: boolean;
}

type NoticeDoc = { title?: string; html?: string };

/** 予約側と揃える：注意事項の文面を更新したらここを更新（キャッシュバスター） */
const NOTICE_VERSION = 'v2025-10-01';

/** public ベースパス（CRA / Vite / 直配信 いずれでも動く） */
function getPublicBase(): string {
  // @ts-ignore
  // eslint-disable-next-line
  const viteBase = process.env.PUBLIC_URL || '';
  // @ts-ignore
  const craBase = (typeof process !== 'undefined' && process?.env?.PUBLIC_URL) || '';
  const base = viteBase || craBase || '/';
  return base.endsWith('/') ? base.slice(0, -1) : base;
}

/** zh 系言語コード（zh, zh-CN など）をすべて zh として扱う */
function normalizeLang(input: string): 'ja' | 'zh' {
  return String(input || '').toLowerCase().startsWith('zh') ? 'zh' : 'ja';
}

const AgaMalePage: React.FC<AgaMalePageProps> = ({ ipAllowed }) => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const lang: 'ja' | 'zh' = normalizeLang(i18n.language);
  const navigate = useNavigate();

  // 薬品価格（DB優先、フォールバックは静的データ）
  const { medicines } = useMedicinePrices('aga_male', lang);

  const [noticeOpen, setNoticeOpen] = useState(false);
  const [agree, setAgree] = useState(false);

  // public/data/notices から文面を取得
  const [doc, setDoc] = useState<NoticeDoc | null>(null);
  const [docLoading, setDocLoading] = useState(false);

  const treatments = (t('aga_male.treatment_items', { returnObjects: true }) as string[]) || [];
  const recommends = (t('aga_male.recommend_items', { returnObjects: true }) as string[]) || [];

  type PriceItem = { name: string; dosage: string; price: number; taxPrice: number };

  // 固定文（JP/中文）
  const L: Record<'ja' | 'zh', {
    sectionTitle: string;
    p1: string;
    p2: string;
    seeFaqPrefix: string;
    seeFaqLink: string;
    seeFaqSuffix: string;
    priceTitle: string;
    priceUnit: string;
    priceItems: PriceItem[];
    badges: string[];
    faqBtn: string;
    noticeBtn: string;
    modalCta: string;
    modalClose: string;
    modalAgree: string;
    cta: string;
  }> = {
    ja: {
      sectionTitle: 'AGA・男性',
      p1: 'AGA（男性型脱毛症）は、毛髪の成長を阻害するDHT（ジヒドロテストステロン）の生成が主因です。フィナステリドは5α-還元酵素（主にII型）を阻害してDHT産生を抑制し、国内試験で改善率54～58％が報告されています。',
      p2: 'デュタステリドはI型/II型の両方を阻害し、増毛効果が約1.6倍との報告もあります。当院では正規ジェネリックを含めた選択肢をご用意し、医師が体質・既往歴を踏まえ最適な治療をご提案します。',
      badges: ['診察料 0円', '24時間オンライン予約', 'スピード発送', '自由診療（保険適用外）'],
      seeFaqPrefix: 'AGAに関するご質問は',
      seeFaqLink: 'FAQ',
      seeFaqSuffix: 'をご覧ください。',
      priceTitle: '価格（診察料 0円）：',
      priceUnit: '円',
      priceItems: [
        { name: 'フィナステリド', dosage: '1mg 30錠(1か月)', price: 4200, taxPrice: 4620 },
        { name: 'デュタステリド', dosage: '0.5mg 30錠(1か月)', price: 7200, taxPrice: 7920 },
        { name: 'ミノキシジル', dosage: '2.5mg 30錠(1か月)', price: 6100, taxPrice: 6710 },
        { name: 'ミノキシジル', dosage: '5mg 30錠(1か月)', price: 8800, taxPrice: 9680 },
      ],
      faqBtn: 'FAQを見る',
      noticeBtn: '注意事項はこちらから',
      modalCta: '同意して予約に進む',
      modalClose: '閉じる',
      modalAgree: '上記を読み、内容に同意します。',
      cta: 'オンライン診療を予約する',
    },
    zh: {
      sectionTitle: 'AGA・男性',
      p1: 'AGA（男性型脱发）的主要原因是会抑制毛发生长的 DHT（二氢睾酮）生成。非那雄胺通过抑制 5α 还原酶（以 II 型为主）来减少 DHT 产生，国内研究报告的改善率为 54～58%。',
      p2: '度他雄胺可同时抑制 I 型/II 型，增发效果约为 1.6 倍亦有报道。本院提供含正规仿制药在内的多种选择，医生会结合体质与既往史为您提出最合适的方案。',
      badges: ['诊察费 0 日元', '24 小时在线预约', '急速发货', '自费诊疗（不适用医保）'],
      seeFaqPrefix: '关于 AGA 的常见问题，请参阅',
      seeFaqLink: 'FAQ',
      seeFaqSuffix: '。',
      priceTitle: '价格（诊察费 0 日元）：',
      priceUnit: '日元',
      priceItems: [
        { name: '非那雄胺 (Finasteride)', dosage: '1mg 30片(1个月)', price: 4200, taxPrice: 4620 },
        { name: '度他雄胺 (Dutasteride)', dosage: '0.5mg 30片(1个月)', price: 7200, taxPrice: 7920 },
        { name: '米诺地尔 (Minoxidil)', dosage: '2.5mg 30片(1个月)', price: 6100, taxPrice: 6710 },
        { name: '米诺地尔 (Minoxidil)', dosage: '5mg 30片(1个月)', price: 8800, taxPrice: 9680 },
      ],
      faqBtn: '查看 FAQ',
      noticeBtn: '查看注意事项',
      modalCta: '同意并继续预约',
      modalClose: '关闭',
      modalAgree: '我已阅读并同意上述内容。',
      cta: '立即预约在线咨询',
    },
  };

  /** 注意事項モーダルを開くときに JSON を取得（JP→なければZHにフォールバック／逆も同様） */
  const openNotice = async () => {
    setAgree(false);
    setNoticeOpen(true);
    try {
      setDocLoading(true);

      const base = getPublicBase();
      const primary = `${base}/data/notices/aga_male.${lang}.json?v=${encodeURIComponent(NOTICE_VERSION)}`;
      const fallback = `${base}/data/notices/aga_male.${lang === 'ja' ? 'zh' : 'ja'}.json?v=${encodeURIComponent(NOTICE_VERSION)}`;

      const tryFetch = async (url: string) => {
        const res = await fetch(url, { cache: 'no-cache' });
        return res.ok ? ((await res.json()) as NoticeDoc) : null;
      };

      let json = await tryFetch(primary);
      if (!json) {
        console.warn('[AgaMalePage] notice primary not found:', primary);
        json = await tryFetch(fallback);
        if (!json) {
          console.warn('[AgaMalePage] notice fallback not found:', fallback);
          setDoc({ title: lang === 'ja' ? '注意事項' : '注意事项', html: '' });
          return;
        }
      }
      setDoc(json);
    } catch (e) {
      console.error('[AgaMalePage] notice load error:', e);
      setDoc({ title: lang === 'ja' ? '注意事項' : '注意事项', html: '' });
    } finally {
      setDocLoading(false);
    }
  };

  /** モーダル表示中は body スクロールをロック & ESC で閉じる */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNoticeOpen(false);
    };
    if (noticeOpen) {
      window.addEventListener('keydown', onKey);
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        window.removeEventListener('keydown', onKey);
        document.body.style.overflow = prev;
      };
    }
  }, [noticeOpen]);

  /** 同意後に予約へ遷移（科目プリセット＋同意済み＋入口属性を state で渡す） */
  const proceedReserve = () => {
    if (!agree) return;
    setNoticeOpen(false);
    goReserve(navigate, ipAllowed, {
      preselectService: 'aga_male',
      preAgreed: true,
      from: 'service', // ← 予約ページ側で「注意事項デフォルト折りたたみ」に使う
    });
  };

  // 価格表は DB(admin設定)優先。ただし「区分(セクション)単位」でフォールバックする:
  // 該当区分にDB薬が1件でもあればDB価格、無ければその区分のハードコード現行価を維持。
  // → 生産で一部の薬だけ登録しても、未登録区分が空にならず現行価が消えない(逐次移行が安全)。
  const dbItems = toSinglePriceItems(medicines, lang);
  const hcItems: any[] = [...L[lang].priceItems];
  const section = (pred: (x: any) => boolean): any[] => {
    const d = dbItems.filter(pred);
    return d.length > 0 ? d : hcItems.filter(pred);
  };

  return (
    <div className="pt-0 sm:pt-10 pb-10 sm:pb-20 bg-[#fff9f8] font-sans" translate="no">
      <Helmet>
        <title>{t('services.aga_male.title')} - Melodis Clinic</title>
        <meta name="description" content={t('seo.aga_male.description')} />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: lang === 'ja' ? 'フィナステリド・デュタステリド (男性AGA治療薬)' : '非那雄胺・度他雄胺 (男性 AGA 治疗药)',
            image: 'https://melodis-clinic.com/images/12.png',
            description: lang === 'ja'
              ? 'フィナステリドやデュタステリドなどの内服薬、ミノキシジル外用薬を用いた男性AGA治療をオンライン診療で提供しています。'
              : '本院通过在线问诊提供非那雄胺、度他雄胺口服药以及米诺地尔外用药等男性 AGA 治疗方案。',
            brand: {
              '@type': 'Brand',
              name: 'Generic AGA Medications'
            },
            offers: {
              '@type': 'Offer',
              url: window.location.href,
              priceCurrency: 'JPY',
              price: '3000',
              availability: 'https://schema.org/InStock',
              seller: {
                '@type': 'MedicalOrganization',
                name: 'Melodis Clinic',
                telephone: '+81-3-6908-6228',
                address: {
                  '@type': 'PostalAddress',
                  streetAddress: '3-23-3 Higashi-Ikebukuro, 804',
                  addressLocality: 'Toshima-ku',
                  addressRegion: 'Tokyo',
                  postalCode: '170-0013',
                  addressCountry: 'JP'
                }
              }
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: lang === 'ja' ? 'フィナステリドとデュタステリドの違いは何ですか？' : '非那雄胺和度他雄胺有什么区别？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: lang === 'ja'
                    ? 'フィナステリドは抜け毛を抑制し、デュタステリドはさらに強力に還元酵素を阻害します。一般的にデュタステリドの方が発毛効果が高いとされています。'
                    : '非那雄胺主要抑制脱发，而度他雄胺对还原酶的阻断作用更为强效。一般来说，度他雄胺的发毛效果更为突出。'
                }
              }
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'MedicalWebPage',
            potentialAction: {
              '@type': 'ReserveAction',
              target: {
                '@type': 'EntryPoint',
                urlTemplate: 'https://melodis-clinic.com/reserve',
                actionPlatform: [
                  'http://schema.org/DesktopWebPlatform',
                  'http://schema.org/MobileWebPlatform'
                ]
              }
            }
          })}
        </script>
      </Helmet>

      <div className="container mx-auto px-0 sm:px-4 max-w-5xl">
        <div className="bg-white shadow-none sm:shadow-md rounded-none sm:rounded-3xl p-5 sm:p-8 md:p-12 border-y sm:border border-slate-100">
          {/* 見出し */}
          <div className="flex items-center mb-6">
            <div className="mr-4 rounded-2xl bg-sky-100 p-3">
              <FaNotesMedical className="text-sky-600 w-7 h-7" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-800">
              <span className="text-sky-400 mr-2">│</span>{t('services.aga_male.title')}
            </h1>
          </div>

          <div className="mb-8 flex items-center gap-3">
            <Link
              to="/about"
              className="inline-flex items-center gap-2 rounded-full bg-slate-50 border border-slate-200 px-3 py-1.5 text-xs md:text-sm text-slate-600 hover:bg-slate-100 transition"
            >
              <span className="w-2 h-2 rounded-full bg-emerald-400" />
              {t('seo.supervision_doctor')}
              <span className="text-[10px] text-slate-400 font-normal ml-1">{t('seo.supervision_title')}</span>
            </Link>
          </div>

          <p className="text-slate-700 text-base md:text-lg leading-relaxed mb-8 bg-[#fff9f8]/50 border-l-4 border-sky-200 pl-4 py-2">
            {t('seo.aga_male.summary')}
          </p>

          {/* 説明（短文） */}
          <p className="text-slate-700 text-base md:text-lg leading-relaxed mb-8">
            {t('aga_male.description')}
          </p>

          {/* 詳細ブロック */}
          <section className="rounded-2xl border border-sky-100 bg-[#fff9f8]/50 p-6 md:p-8 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <FaInfoCircle className="text-sky-600 w-5 h-5" />
              <h2 className="text-2xl font-semibold text-sky-700">{L[lang].sectionTitle}</h2>
            </div>

            {/* badges */}
            <ul className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 text-sm">
              {L[lang].badges.map((b, i) => (
                <li
                  key={i}
                  className="rounded-xl border border-sky-200 bg-white px-4 py-3 text-slate-800 text-center"
                >
                  {b}
                </li>
              ))}
            </ul>

            <div className="space-y-4 text-slate-800 leading-relaxed mb-8">
              <p>{L[lang].p1}</p>
              <p>{L[lang].p2}</p>
              <p>
                {L[lang].seeFaqPrefix}
                <button
                  type="button"
                  onClick={() => navigate('/faq/aga-male')}
                  className="underline decoration-sky-300 underline-offset-2 hover:text-sky-700 mx-1"
                >
                  {L[lang].seeFaqLink}
                </button>
                {L[lang].seeFaqSuffix}
              </p>
            </div>

            {/* 価格・リンク */}
            <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="text-xl font-semibold text-slate-800 mb-3">{L[lang].priceTitle}</h3>
              <div className="space-y-8">
                <div>
                  <h4 className="font-bold text-sky-700 mb-3 border-b border-sky-100 pb-1">
                    {lang === 'ja' ? '主成分内服薬' : '核心治疗药物'}
                  </h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    {section(item => item.name.includes('フィナステリド') || item.name.includes('デュタステリド') || item.name.includes('非那雄胺') || item.name.includes('度他雄胺')).map((item, i) => (
                      <div key={i} className="rounded-xl bg-sky-50/30 p-4 border border-sky-100 flex flex-col hover:border-sky-200 transition">
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-bold text-slate-800">{item.name}</span>
                        </div>
                        <div className="text-xs text-slate-500 mb-3">{item.dosage}</div>
                        <div className="mt-auto text-right">
                          <div className="flex items-baseline justify-end gap-1">
                            <span className="font-bold text-lg text-slate-900">{item.price.toLocaleString()}</span>
                            <span className="text-sm text-slate-600">{lang === 'ja' ? '円' : ' 日元'}</span>
                          </div>
                          <div className="text-[11px] text-slate-400 font-normal">
                            ({lang === 'ja' ? '税込' : '含税'} {item.taxPrice.toLocaleString()}{lang === 'ja' ? '円' : ' 日元'})
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-bold text-sky-700 mb-3 border-b border-sky-100 pb-1">
                    {lang === 'ja' ? '補助・発毛促進薬' : '辅助治疗与促发药物'}
                  </h4>
                  <ul className="space-y-3 text-slate-700">
                    {section(item => item.name.includes('ミノキシジル') || item.name.includes('米诺地尔')).map((item, i) => (
                      <li key={i} className="flex justify-between items-start flex-wrap gap-2 py-1">
                        <span className="flex flex-col text-left">
                          <span className="font-bold text-slate-800">{item.name}</span>
                          <span className="text-[11px] text-slate-500">{item.dosage}</span>
                        </span>
                        <div className="text-right">
                          <div className="flex items-baseline gap-1 justify-end">
                            <span className="font-bold text-slate-900">{item.price.toLocaleString()}</span>
                            <span className="text-sm text-slate-600">{lang === 'ja' ? '円' : ' 日元'}</span>
                          </div>
                          <div className="text-[10px] text-slate-400 font-normal">
                            ({lang === 'ja' ? '税込' : '含税'} {item.taxPrice.toLocaleString()}{lang === 'ja' ? '円' : ' 日元'})
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  onClick={() => navigate('/faq/aga-male')}
                  className="inline-flex items-center gap-2 rounded-full border border-sky-300 bg-[#fff9f8] px-4 py-2 text-sm font-medium text-sky-700 hover:bg-sky-100"
                >
                  {L[lang].faqBtn} <FaArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={openNotice}
                  className="inline-flex items-center gap-2 rounded-full border border-rose-300 bg-rose-50 px-4 py-2 text-sm font-medium text-rose-700 hover:bg-rose-100"
                >
                  {L[lang].noticeBtn} <FaArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </section>

          {/* 治療項目（タグ） */}
          <div className="rounded-2xl p-6 md:p-8 mb-8 border border-sky-100 bg-[#fff9f8]">
            <h2 className="text-2xl font-semibold text-sky-700 mb-4">
              {t('aga_male.treatment_title')}
            </h2>
            <div className="flex flex-wrap gap-3">
              {treatments.map((item, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-white px-4 py-2 text-sm text-slate-800"
                >
                  <FaCheckCircle className="text-sky-500 w-4 h-4" />
                  {item}
                </span>
              ))}
            </div>
          </div>

          {/* おすすめ対象 */}
          <div className="rounded-2xl p-6 md:p-8 bg-slate-50 border border-slate-100">
            <h3 className="text-xl font-semibold text-slate-800 mb-4">
              {t('aga_male.recommend_title')}
            </h3>
            <ul className="grid md:grid-cols-2 gap-3">
              {recommends.map((item, idx) => (
                <li
                  key={idx}
                  className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-700"
                >
                  <FaCheckCircle className="text-sky-500 w-5 h-5 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* 予約 CTA（→ モーダル経由） */}
          {isAuthenticated && (
            <div className="mt-10 text-center">
              <button
                onClick={openNotice}
                className="inline-block bg-sky-600 hover:bg-sky-600 active:bg-sky-700 text-white px-10 py-3.5 text-base md:text-lg rounded-full font-semibold shadow-sm transition"
              >
                {L[lang].cta}
              </button>
              <div className="mx-auto mt-3 h-1 w-20 rounded-full bg-rose-300/60" />
            </div>
          )}
        </div>
      </div>

      {/* 注意事項モーダル（public/data から読み込み） */}
      {noticeOpen && (
        <div
          className="fixed inset-0 z-[1000] overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="notice-title"
          onClick={() => setNoticeOpen(false)}
        >
          {/* オーバーレイ */}
          <div className="fixed inset-0 bg-black/40" />
          {/* コンテンツ */}
          <div
            className="min-h-full flex items-start md:items-center justify-center p-4 md:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative z-[1001] w-full max-w-2xl bg-white rounded-2xl shadow-xl p-6 md:p-7 border border-slate-200 max-h-[90vh] overflow-y-auto">
              <div className="flex items-start justify-between gap-4 mb-3">
                <h3 id="notice-title" className="text-xl md:text-2xl font-semibold text-slate-900">
                  {doc?.title || (lang === 'ja' ? '注意事項' : '注意事项')}
                </h3>
                <button
                  onClick={() => setNoticeOpen(false)}
                  aria-label="Close"
                  className="rounded-full px-3 py-1.5 bg-slate-100 text-slate-600 hover:bg-slate-200"
                >
                  ×
                </button>
              </div>

              <div className="prose prose-slate max-w-none text-[15px] leading-relaxed">
                {docLoading ? (
                  <p className="text-slate-500">{lang === 'ja' ? '読み込み中…' : '加载中…'}</p>
                ) : doc?.html ? (
                  <div dangerouslySetInnerHTML={{ __html: doc.html }} />
                ) : (
                  <p className="text-slate-500">
                    {lang === 'ja' ? 'この項目の注意事項は現在準備中です。' : '该项目的注意事项正在准备中。'}
                  </p>
                )}
              </div>

              <label className="mt-4 flex items-start gap-2 text-slate-700">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                />
                <span>{L[lang].modalAgree}</span>
              </label>

              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  disabled={!agree}
                  onClick={proceedReserve}
                  className={`inline-flex items-center justify-center rounded-full px-6 py-2.5 font-semibold text-white transition ${agree ? 'bg-sky-600 hover:bg-sky-600 active:bg-sky-700' : 'bg-sky-300 cursor-not-allowed'
                    }`}
                >
                  {L[lang].modalCta}
                </button>
                <button
                  onClick={() => setNoticeOpen(false)}
                  className="rounded-full border border-slate-300 px-6 py-2.5 text-slate-700 hover:bg-slate-50"
                >
                  {L[lang].modalClose}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgaMalePage;
